import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import {
	baseLocale,
	getLocale,
	isLocale,
	localStorageKey,
	setLocale as paraglideSetLocale,
	locales,
	type Locale,
} from '@/paraglide/runtime';

type LocaleContextValue = {
	locale: Locale;
	setLocale: (locale: Locale) => void;
	availableLocales: readonly Locale[];
};

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

const resolveInitialLocale = (): Locale => {
	if (typeof window === 'undefined') return baseLocale;
	const stored = window.localStorage.getItem(localStorageKey);
	if (stored && isLocale(stored)) return stored as Locale;
	window.localStorage.setItem(localStorageKey, baseLocale);
	return baseLocale;
};

export const LocaleProvider = ({ children }: { children: ReactNode }) => {
	const [locale, setLocaleState] = useState<Locale>(() => {
		const initial = resolveInitialLocale();
		const current = getLocale();
		if (current !== initial) {
			paraglideSetLocale(initial, { reload: false });
		}
		return initial;
	});

	const setLocale = useCallback((next: Locale) => {
		paraglideSetLocale(next, { reload: false });
		setLocaleState(next);
	}, []);

	const value = useMemo<LocaleContextValue>(
		() => ({ locale, setLocale, availableLocales: locales }),
		[locale, setLocale],
	);

	return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = (): LocaleContextValue => {
	const ctx = useContext(LocaleContext);
	if (!ctx) {
		throw new Error('useLocale must be used within a LocaleProvider');
	}
	return ctx;
};
